import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import {
  Youtube as YoutubeIcon, CheckCircle2, AlertTriangle, Play, Pause,
  RotateCcw, Link as LinkIcon, ExternalLink, Loader2, Settings,
  Upload, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const cfg = {
    success: { c: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40", l: "Publicado" },
    processing: { c: "bg-blue-500/20 text-blue-400 border-blue-500/40", l: "Processando" },
    failed: { c: "bg-amber-500/20 text-amber-400 border-amber-500/40", l: "Falhou" },
    failed_permanent: { c: "bg-red-500/20 text-red-400 border-red-500/40", l: "Falhou (3x)" },
    waiting_quota_reset: { c: "bg-purple-500/20 text-purple-300 border-purple-500/40", l: "Aguarda quota" },
    pending: { c: "bg-zinc-500/20 text-zinc-400 border-zinc-500/40", l: "Pendente" },
  };
  const s = cfg[status] || cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${s.c}`}>
      {s.l}
    </span>
  );
}

export default function YouTubeSync() {
  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [config, setConfig] = useState(null);
  const [autoStatus, setAutoStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [cds, setCds] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [clientIdInput, setClientIdInput] = useState("");
  const [clientSecretInput, setClientSecretInput] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [c, a, s, list, pls] = await Promise.all([
        axios.get(`${API}/admin/youtube/config`, auth),
        axios.get(`${API}/admin/youtube/auto-sync/status`, auth),
        axios.get(`${API}/admin/youtube/stats`, auth),
        axios.get(`${API}/admin/youtube/cds`, auth),
        axios.get(`${API}/admin/youtube/playlists`, auth),
      ]);
      setConfig(c.data);
      setAutoStatus(a.data);
      setStats(s.data);
      setCds(list.data || []);
      setPlaylists(pls.data || []);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let intervalId = null;
    const run = async () => { if (mounted) await refresh(); };
    run();
    intervalId = setInterval(run, 5000);
    return () => { mounted = false; if (intervalId) clearInterval(intervalId); };
  }, [refresh]);

  const saveConfig = async () => {
    if (!clientIdInput || !clientSecretInput) {
      toast.error("Informe Client ID e Client Secret");
      return;
    }
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/youtube/config`,
        { client_id: clientIdInput.trim(), client_secret: clientSecretInput.trim() }, auth);
      toast.success("Configuração salva. Clique em 'Conectar' agora.");
      setClientIdInput("");
      setClientSecretInput("");
      refresh();
    } catch (e) {
      toast.error("Erro ao salvar credenciais");
    }
    setSavingConfig(false);
  };

  const connect = async () => {
    try {
      const r = await axios.get(`${API}/admin/youtube/oauth-url`, auth);
      window.open(r.data.oauth_url, "_blank", "width=600,height=700");
      toast.info("Autorize na janela do Google. Atualizo em alguns segundos.");
      setTimeout(refresh, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar URL");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar conta YouTube?")) return;
    await axios.post(`${API}/admin/youtube/disconnect`, {}, auth);
    toast.success("Desconectado");
    refresh();
  };

  const toggleAuto = async () => {
    if (autoStatus?.enabled) {
      await axios.post(`${API}/admin/youtube/auto-sync/pause`, {}, auth);
      toast.success("Auto-sync PAUSADA");
    } else {
      await axios.post(`${API}/admin/youtube/auto-sync/resume`, {}, auth);
      toast.success("Auto-sync ATIVADA");
    }
    refresh();
  };

  const toggleApprove = async (cd) => {
    const next = !cd.youtube_approved;
    await axios.post(`${API}/admin/youtube/cd/${cd.id}/approve`, { approved: next }, auth);
    toast.success(next ? `${cd.cd_title} aprovado` : `${cd.cd_title} reprovado`);
    refresh();
  };

  const syncNow = async (cd) => {
    await axios.post(`${API}/admin/youtube/sync-cd/${cd.id}?force=true`, {}, auth);
    toast.info(`Sincronização do "${cd.cd_title}" iniciada (acompanhe abaixo)`);
    refresh();
  };

  const resetAttempts = async (cd) => {
    await axios.post(`${API}/admin/youtube/cd/${cd.id}/reset-attempts`, {}, auth);
    toast.success("Tentativas resetadas");
    refresh();
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3">
              <YoutubeIcon size={32} className="text-red-500" /> YouTube Auto-Sync
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">
              Envia álbuns aprovados para o canal do ArrochaPlay no YouTube — 1 vídeo por CD com timeline na descrição.
            </p>
          </div>
          <button
            onClick={refresh}
            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-xs font-bold border border-white/10"
          >Atualizar</button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2"/>Carregando...</div>
        ) : (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <StatCard label="CDs total" value={stats?.total_cds || 0} icon={Upload} />
              <StatCard label="No YouTube" value={stats?.synced_to_youtube || 0} icon={CheckCircle2} accent="text-emerald-400" />
              <StatCard label="Na fila" value={autoStatus?.pending_in_queue || 0} icon={Clock} accent="text-cyan-400" />
              <StatCard label="Hoje" value={`${autoStatus?.uploads_today || 0}/${autoStatus?.max_uploads_per_day || 6}`} icon={Upload} accent="text-amber-400" />
            </div>

            {/* Conexão */}
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 mb-6">
              <h2 className="font-black text-lg mb-3 flex items-center gap-2">
                <Settings size={18}/> Conexão com YouTube
              </h2>
              {config?.is_connected ? (
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold">
                    <CheckCircle2 size={18}/> Conectado{config?.authenticated_at ? ` em ${new Date(config.authenticated_at).toLocaleString("pt-BR")}` : ""}
                  </div>
                  <button onClick={disconnect} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 rounded-lg text-sm font-bold" data-testid="yt-disconnect">
                    Desconectar
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-amber-300/90 text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle size={14}/> Cole abaixo o Client ID e o Client Secret criados em
                    <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-[#FF007F] hover:underline ml-1">Google Cloud Console</a>.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text" placeholder="Client ID (xxxxxx.apps.googleusercontent.com)"
                      defaultValue={config?.client_id || ""} value={clientIdInput || config?.client_id || ""}
                      onChange={(e) => setClientIdInput(e.target.value)}
                      className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none"
                      data-testid="yt-client-id"
                    />
                    <input
                      type="password" placeholder={config?.client_secret_set ? "•••••••• (já salvo - sobrescrever)" : "Client Secret"}
                      value={clientSecretInput}
                      onChange={(e) => setClientSecretInput(e.target.value)}
                      className="bg-black border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#FF007F] focus:outline-none"
                      data-testid="yt-client-secret"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={saveConfig} disabled={savingConfig}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg text-sm font-bold disabled:opacity-50"
                      data-testid="yt-save-config">
                      {savingConfig ? "Salvando..." : "Salvar credenciais"}
                    </button>
                    <button onClick={connect}
                      className="px-4 py-2 bg-[#FF007F] hover:bg-[#FF3399] text-white rounded-lg text-sm font-bold flex items-center gap-2"
                      data-testid="yt-connect">
                      <LinkIcon size={14}/> Conectar com Google
                    </button>
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-3">
                    Redirect URI esperado: <code className="text-[#FF007F]">https://arrochaplay.com.br/api/admin/youtube/callback</code>
                  </p>
                </>
              )}
            </div>

            {/* Auto-sync toggle */}
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-black text-lg mb-1">Sincronização Automática</h2>
                  <p className="text-zinc-500 text-xs">
                    {autoStatus?.enabled ? (
                      <>Worker rodando · Próximo upload elegível: {autoStatus?.next_upload_eligible_at ? new Date(autoStatus.next_upload_eligible_at).toLocaleString("pt-BR") : "agora"}</>
                    ) : "Pausada"}
                  </p>
                  {autoStatus?.last_error && (<p className="text-amber-400 text-xs mt-1">Último erro: {autoStatus.last_error}</p>)}
                </div>
                <button onClick={toggleAuto} disabled={!config?.is_connected}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed ${
                    autoStatus?.enabled
                      ? "bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 text-amber-300"
                      : "bg-emerald-500 hover:bg-emerald-400 text-black"
                  }`}
                  data-testid="yt-auto-toggle"
                >
                  {autoStatus?.enabled ? (<><Pause size={14}/> Pausar Auto-Sync</>) : (<><Play size={14}/> Ativar Auto-Sync</>)}
                </button>
              </div>
            </div>

            {/* Playlists por artista */}
            {playlists.length > 0 && (
              <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 mb-6">
                <h2 className="font-black text-lg mb-3 flex items-center gap-2">
                  <YoutubeIcon size={18} className="text-red-500"/> Playlists no canal ({playlists.length})
                </h2>
                <p className="text-zinc-500 text-xs mb-4">
                  O sistema cria uma playlist no YouTube para cada artista. Novos CDs entram nela automaticamente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {playlists.map((p) => (
                    <a
                      key={p.playlist_id}
                      href={p.playlist_url}
                      target="_blank"
                      rel="noreferrer"
                      className="group p-3 rounded-xl bg-black/40 border border-white/5 hover:border-red-500/50 transition flex items-center gap-3"
                      data-testid={`yt-playlist-${p.playlist_id}`}
                    >
                      <div className="w-10 h-10 rounded bg-red-500/20 flex items-center justify-center text-red-400 flex-shrink-0">
                        <YoutubeIcon size={18}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">{p.artist_name}</p>
                        <p className="text-zinc-500 text-[11px] truncate">{p.video_count || 0} vídeos</p>
                      </div>
                      <ExternalLink size={14} className="text-zinc-500 group-hover:text-red-400 flex-shrink-0"/>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Lista de CDs */}
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5">
              <h2 className="font-black text-lg mb-4">CDs do catálogo ({cds.length})</h2>
              <div className="space-y-2">
                {cds.map((cd) => (
                  <div key={cd.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-black/40 border border-white/5 hover:border-white/15 transition"
                    data-testid={`yt-cd-${cd.id}`}
                  >
                    {cd.cover_url ? (
                      <img src={cd.cover_url.startsWith("http") ? cd.cover_url : `${process.env.REACT_APP_BACKEND_URL}${cd.cover_url}`}
                        alt="" className="w-12 h-12 rounded object-cover flex-shrink-0"/>
                    ) : (<div className="w-12 h-12 rounded bg-white/5 flex-shrink-0"/>)}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-bold truncate">{cd.cd_title}</p>
                      <p className="text-zinc-500 text-xs truncate">
                        {cd.artist_name} · {cd.track_count || 0} faixas
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <StatusBadge status={cd.youtube_sync_status} />
                        {cd.youtube_attempts > 0 && (
                          <span className="text-[10px] text-zinc-500">tentativas: {cd.youtube_attempts}/3</span>
                        )}
                        {cd.youtube_last_error && (
                          <span className="text-[10px] text-amber-400 truncate max-w-[260px]" title={cd.youtube_last_error}>
                            ⚠ {cd.youtube_last_error.slice(0, 60)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {cd.youtube_url ? (
                        <a href={cd.youtube_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 rounded-lg text-xs font-bold">
                          <ExternalLink size={12}/> Abrir
                        </a>
                      ) : (
                        <>
                          <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer select-none">
                            <input type="checkbox" checked={!!cd.youtube_approved}
                              onChange={() => toggleApprove(cd)}
                              data-testid={`yt-approve-${cd.id}`}
                              className="accent-[#FF007F]" />
                            Aprovado
                          </label>
                          <button onClick={() => syncNow(cd)} disabled={!config?.is_connected}
                            className="px-3 py-1.5 bg-[#FF007F] hover:bg-[#FF3399] disabled:opacity-30 text-white rounded-lg text-xs font-bold flex items-center gap-1"
                            data-testid={`yt-sync-${cd.id}`}
                          >
                            <Upload size={12}/> Subir
                          </button>
                          {(cd.youtube_attempts || 0) > 0 && (
                            <button onClick={() => resetAttempts(cd)} title="Resetar contador de tentativas"
                              className="px-2 py-1.5 bg-white/5 hover:bg-white/10 text-zinc-300 rounded-lg text-xs">
                              <RotateCcw size={12}/>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {cds.length === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-8">Nenhum CD aprovado no catálogo.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent = "text-[#FF007F]" }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 flex items-center gap-3">
      <Icon size={20} className={accent}/>
      <div>
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">{label}</p>
        <p className="text-xl font-black text-white tabular-nums">{value}</p>
      </div>
    </div>
  );
}
